/**
 * Secret / token / PII scrubbing for diagnostic capture.
 *
 * Console-error and failed-network buffers are auto-attached to issue reports, so they must NOT
 * leak bearer tokens, passwords, API keys, JWTs, or query-string credentials. Everything captured
 * passes through `scrubText` (free text) or `scrubUrl` (URLs) first.
 *
 * Conservative by design: over-redaction is fine, leaking is not.
 */

const REDACTED = '«redacted»';
const MAX_LEN = 2000;

/** Query/body keys whose VALUES are always sensitive. */
const SENSITIVE_KEY_RE =
  /(pass(word)?|pwd|secret|token|refresh|authorization|auth|api[-_]?key|client[-_]?secret|access[-_]?token|id[-_]?token|session|cookie|otp|ssn|card|cvv)/i;

// Raw JWT (three base64url segments) — redacted first so the bearer rule doesn't half-eat it.
const JWT_RE = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
// Authorization: Bearer <token>  /  "bearer eyJ..."
const BEARER_RE = /\b[Bb]earer\s+[A-Za-z0-9._~+/-]+=*/g;
// key=value / key: value / "key":"value" where the key looks sensitive (keeps key, redacts value).
// The `["']?` after the key tolerates a closing quote before the delimiter so JSON-style
// `"password":"hunter2"` is matched (the quote previously broke the `\s*[=:]` match).
const KEYVAL_RE =
  /\b(pass(?:word)?|pwd|secret|token|refresh[-_]?token|access[-_]?token|api[-_]?key|client[-_]?secret|authorization|auth|session|cookie|otp)\b["']?\s*[=:]\s*("[^"]*"|'[^']*'|[^\s,&;]+)/gi;
// Email addresses (PII).
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

/** Redact secrets from a free-text string. Always returns a string. */
export function scrubText(input: string | null | undefined): string {
  if (input === null || input === undefined) return '';
  let out = input;
  out = out.replace(JWT_RE, REDACTED);
  out = out.replace(BEARER_RE, REDACTED);
  out = out.replace(KEYVAL_RE, (_full: string, key: string) => `${key}=${REDACTED}`);
  out = out.replace(EMAIL_RE, REDACTED);
  return out.length > MAX_LEN ? `${out.slice(0, MAX_LEN)}…` : out;
}

/**
 * Redact credentials from a URL: strip the query-string values for sensitive keys and any
 * userinfo segment, keeping the path for debugging context. Falls back to text scrubbing on a
 * non-parseable URL.
 */
export function scrubUrl(input: string | null | undefined): string {
  if (input === null || input === undefined) return '';
  const raw = input;
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const u = new URL(raw, origin);
    u.username = '';
    u.password = '';
    for (const key of Array.from(u.searchParams.keys())) {
      if (SENSITIVE_KEY_RE.test(key)) {
        u.searchParams.set(key, REDACTED);
      }
    }
    // Re-run text scrub over the rebuilt URL for any inline tokens.
    return scrubText(`${u.origin}${u.pathname}${u.search}`);
  } catch {
    return scrubText(raw);
  }
}

export const SCRUB_REDACTED = REDACTED;
